'use client';

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type BreakpointKey = keyof typeof breakpoints | 'xs';

type Orientation = 'portrait' | 'landscape';

interface ViewportState {
  width: number;
  breakpoint: BreakpointKey;
  orientation: Orientation;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  _set: (w: number) => void;
}

function derive(width: number): Omit<ViewportState, 'width' | '_set'> {
  const breakpoint =
    width < breakpoints.sm
      ? 'xs'
      : (Object.keys(breakpoints) as (keyof typeof breakpoints)[])
          .reverse()
          .find((k) => width >= breakpoints[k]) ?? 'xs';

  return {
    breakpoint,
    orientation:
      typeof window !== 'undefined' && window.innerHeight > width
        ? 'portrait'
        : 'landscape',
    isMobile: width < breakpoints.md,
    isTablet: width >= breakpoints.md && width < breakpoints.lg,
    isDesktop: width >= breakpoints.lg,
  };
}

export const useViewportStore = create<ViewportState>()(
  subscribeWithSelector((set) => {
    const width = typeof window === 'undefined' ? 0 : window.innerWidth;
    return {
      width,
      ...derive(width),
      _set: (w: number) => set({ width: w, ...derive(w) }),
    };
  })
);

if (typeof window !== 'undefined') {
  let raf = 0;
  const onResize = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() =>
      useViewportStore.getState()._set(window.innerWidth)
    );
  };
  window.addEventListener('resize', onResize, { passive: true });
}

export const useViewport = <T,>(selector: (state: ViewportState) => T) =>
  useViewportStore(selector);

export const useIsMobile = () => useViewport((s) => s.isMobile);
export const useBreakpoint = () => useViewport((s) => s.breakpoint);
